import { faCheck, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { Button } from "@mui/material";
import { uniqBy } from "lodash";
import { ComponentRef, useMemo, useRef, useState } from "react";
import { useBoolean, useEventCallback } from "usehooks-ts";

import type { ImageTransform } from "@/types";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  Divider,
  Icon,
} from "..";
import { PresetType } from "../AspectRatio";
import { mapPresetTypeToAspectRatio } from "./components/AspectRatioPicker/aspect-ratio";
import { ImageEditor, ImageEditorProps } from "./ImageEditor";

export interface ImageEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;

  image: string; // image url
  onApply: (
    image: ImageEditorDialogProps["image"],
    transform: ImageEditorDialogProps["transform"],
    preset: ImageEditorDialogProps["preset"],
  ) => void;

  presets?: PresetType[];
  preset?: PresetType;

  transform?: ImageTransform;
}

export const ImageEditorDialog = (props: ImageEditorDialogProps) => {
  const {
    isOpen,
    onClose,
    onApply,
    image,
    presets,
    preset,
    // onPresetChange,
    transform,
  } = props;

  const [internalTransform, setInternalTransform] =
    useState<ImageEditorDialogProps["transform"]>(transform);
  const [internalAspectRatio, setInternalAspectRatio] =
    useState<ImageEditorProps["aspectRatio"]>();

  const ref = useRef<ComponentRef<typeof ImageEditor>>(null);

  const {
    value: isApplying,
    setTrue: setIsApplyingTrue,
    setFalse: setIsApplyingFalse,
  } = useBoolean();

  const handleClose = useEventCallback(() => {
    if (isApplying) return; // prevent the dialog from closing while applying
    onClose();
  });

  const handleApply = useEventCallback(() => {
    setIsApplyingTrue();

    // Find the preset that matches the current aspect ratio
    const currentAspectRatio = internalAspectRatio || aspectRatio;
    const ar = currentAspectRatio
      ? currentAspectRatio.width / currentAspectRatio.height
      : undefined;
    const matchingPreset = ar
      ? presets?.find((p) => p.aspectRatio === ar)
      : preset;

    onApply(image, internalTransform, matchingPreset);

    setIsApplyingFalse();
  });

  // Aspect ratio to PresetType conversion
  const aspectRatios = useMemo(() => {
    if (!presets) return;

    const ars = presets
      .map(mapPresetTypeToAspectRatio)
      .filter((ar) => ar !== undefined);
    return uniqBy(ars, "type");
  }, [presets]);

  const aspectRatio = useMemo<ImageEditorProps["aspectRatio"]>(() => {
    // Use internal aspect ratio if set, otherwise fall back to preset-based calculation
    if (internalAspectRatio) return internalAspectRatio;

    if (!preset || !aspectRatios) return;

    const ar = mapPresetTypeToAspectRatio(preset);

    if (!ar) {
      return aspectRatios[0];
    }

    return ar;
  }, [internalAspectRatio, aspectRatios, preset]);

  const handleAspectRatioChange: ImageEditorProps["onAspectRatioChange"] =
    useEventCallback((nextAspectRatio) => {
      // Update the internal aspect ratio immediately for instant UI feedback
      setInternalAspectRatio(nextAspectRatio);
    });

  return (
    <Dialog fullWidth maxWidth="md" open={isOpen} onClose={handleClose}>
      <DialogHeader title="Edit Image" onClose={handleClose} />
      <DialogContent sx={{ padding: 0 }}>
        <Divider />
        <ImageEditor
          image={image}
          ref={ref}
          aspectRatio={aspectRatio}
          aspectRatios={aspectRatios}
          onAspectRatioChange={handleAspectRatioChange}
          transform={internalTransform}
          onTransformChange={setInternalTransform}
        />
        <Divider />
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isApplying}
          variant="contained"
          color="secondary"
          onClick={handleClose}
          startIcon={<Icon icon={faXmark} />}
        >
          Cancel
        </Button>
        <Button
          loading={isApplying}
          variant="contained"
          color="primary"
          onClick={handleApply}
          startIcon={<Icon icon={faCheck} />}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
